import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import {
  MICHELIN_GET_HOTEL_CARHIRE_CREATE_SEARCH,
  MICHELIN_GET_HOTEL_CARHIRE_POLLING,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { capitalizeFirstLetter } from '@web-platform/shared-universal-utils-frontend/src/helpers/capitalizeFirstLetter';

import { USER_REQUESTED_SEARCH } from '../../../../actions/searchControls';

import type { SkyscannerMichelinSearchV1Search } from '@skyscanner-internal/michelin-web-http-client';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type {
  CarHireEntity,
  CarHireFilterPill,
  CarHireState,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/carHire';

type SearchResponse = SkyscannerMichelinSearchV1Search.SearchResponse;

export const INITIAL_STATE: CarHireState = {
  byBucket: {},
  filterPills: [],
  selectedFilterPill: '',
  selectedPillIndex: 0,
  isLoading: true,
};

const transformCarHireData = (response: SearchResponse, state: CarHireState): CarHireState => {
  if (!response || !response.sections) return state;

  const carHireSection = response.sections.find((section) => section && section.carhire);

  if (!carHireSection || !carHireSection.carhire || !carHireSection.context) return state;

  const { carhire, context } = carHireSection;
  const { polling } = context;

  if (!polling?.complete || !polling?.modified) return state;

  const byBucket: Record<string, CarHireEntity[]> = {};

  const pills = new Map<string, CarHireFilterPill>();

  (carhire.results || []).forEach((car) => {
    if (!car || !car.carDetails || !car.dailyQuote || !car.totalQuote) return;

    const bucketName = car.displayName || '';

    const existingPill = pills.get(bucketName);
    if (existingPill) {
      existingPill.filterPillScore += car.score || 0;
    } else {
      pills.set(bucketName, {
        logicalName: bucketName,
        filterPillScore: car.score || 0,
        tagKey: car.id || bucketName,
      });
    }

    if (!byBucket[bucketName]) {
      byBucket[bucketName] = [];
    }

    byBucket[bucketName].push({
      id: `${bucketName}-${byBucket[bucketName].length}`,
      name: bucketName,
      formattedSubCarType: capitalizeFirstLetter(car.carDetails.formattedSubCarType || ''),
      image: car.imageUri || '',
      price: Math.round(Number(car.dailyQuote.price?.amount) / 100),
      bags: car.carDetails.bags || 0,
      formattedDoors: car.carDetails.formattedDoors || '',
      formattedCarClass: car.carDetails.formattedCarClass || '',
      formattedPrice: car.dailyQuote.formattedPrice || '',
      formattedTotalPrice: car.totalQuote.formattedPrice || '',
      transmission: car.carDetails.transmission || '',
      redirectUrl: car.redirectUri || '',
      seats: car.carDetails.seats || 0,
      vendorLogoUri: car.vendorLogoUri || undefined,
      privateOwner: car.privateOwner || false,
    });
  });

  const bucketNames = Object.keys(byBucket);

  return {
    byBucket,
    filterPills: Array.from(pills.values()),
    selectedFilterPill: bucketNames.length > 0 ? bucketNames[0] : '',
    selectedPillIndex: 0,
    isLoading: false,
  };
};

export default (state: CarHireState = INITIAL_STATE, action: Action): CarHireState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      const { payload } = action;

      if (
        (payload.type === MICHELIN_GET_HOTEL_CARHIRE_CREATE_SEARCH ||
          payload.type === MICHELIN_GET_HOTEL_CARHIRE_POLLING) &&
        payload.response?.body &&
        typeof payload.response.body === 'object' &&
        'sections' in payload.response.body
      ) {
        return transformCarHireData(payload.response.body, state);
      }

      return state;
    }

    case USER_REQUESTED_SEARCH: {
      const { payload } = action;
      if (payload.searched) {
        return INITIAL_STATE;
      }
      return state;
    }

    default:
      return state;
  }
};

export const get = (state: CarHireState): CarHireState => state;
